import React from 'react';
import { useAuth } from '../contexts/AuthContext';

// React reconciliation key for the unauthenticated/loading state. Never
// rendered — an internal sentinel so the key is a stable, non-empty string.
const ANONYMOUS_SUBJECT_KEY = 'anonymous';
// Internal sentinel for a subject with no assigned organization. Never rendered.
const NO_ORG_KEY = 'no-org';

// Account-subject boundary. Keys the authenticated/protected application
// subtree by the Cognito subject (user.id) AND organization (user.org_id) —
// never by token or revision — so:
//   * a cross-subject switch (A → B) remounts all account-scoped component
//     state (empty-dependency AWS load effects in Dashboard/Devices, React Query
//     account caches, etc.), forcing B to refetch its own data;
//   * a same-subject organization switch (org A → org B) also remounts, so
//     account-scoped caches cannot leak across a tenant reassignment;
//   * a same-subject/same-org token refresh (B1 → B2) keeps the key stable and
//     does NOT remount the account-scoped application.
// `anonymous` covers the unauthenticated/loading state so an initial login only
// remounts once, at subject establishment.
const AccountBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const scope = user ? `${user.id}:${user.org_id ?? NO_ORG_KEY}` : ANONYMOUS_SUBJECT_KEY;
  return <React.Fragment key={scope}>{children}</React.Fragment>;
};

export default AccountBoundary;
